import { useCallback, useEffect, useState } from "react";
import { getAllPaymentMethods } from "../services/api";
import type {
  IAllPaymentMethods,
  IBankAccountPaymentMethod,
  ICardPaymentMethod,
  ILastUsedFunding,
} from "../interfaces/payment-method";

/** Deduplicate concurrent fetches for the same linkId only (no sticky cache). */
let inflight: { linkId: string; promise: Promise<IAllPaymentMethods> } | null =
  null;

const fetchPaymentMethods = (linkId: string): Promise<IAllPaymentMethods> => {
  if (inflight && inflight.linkId === linkId) {
    return inflight.promise;
  }

  const promise = getAllPaymentMethods(linkId)
    .then((data) => {
      if (inflight?.linkId === linkId) inflight = null;
      return data;
    })
    .catch((err) => {
      if (inflight?.linkId === linkId) inflight = null;
      throw err;
    });

  inflight = { linkId, promise };
  return promise;
};

export interface UsePaymentMethodsResult {
  /** Enabled cards only. */
  cards: ICardPaymentMethod[];
  bankAccounts: IBankAccountPaymentMethod[];
  lastUsedFunding: ILastUsedFunding | null;
  /** True while the current link has no resolved data yet. */
  loading: boolean;
  /** Force a re-fetch from the backend (e.g. after registering a card). */
  refresh: () => Promise<void>;
}

/**
 * Loads funding instruments from the backend for the claimed link.
 * Revalidates on every mount; exposes `refresh` after register / picker open.
 */
export const usePaymentMethods = (
  linkId: string | null | undefined
): UsePaymentMethodsResult => {
  const [fetched, setFetched] = useState<{
    linkId: string;
    data: IAllPaymentMethods;
  } | null>(null);
  const [loading, setLoading] = useState(!!linkId);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await fetchPaymentMethods(id);
      setFetched({ linkId: id, data });
    } catch {
      /* keep last known state for this link on failure */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!linkId) {
      setFetched(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    fetchPaymentMethods(linkId)
      .then((data) => {
        if (active) setFetched({ linkId, data });
      })
      .catch(() => {
        /* keep last known state on failure */
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [linkId]);

  const refresh = useCallback(async () => {
    if (!linkId) return;
    await load(linkId);
  }, [linkId, load]);

  const data =
    fetched && fetched.linkId === linkId ? fetched.data : null;

  return {
    cards: (data?.cards ?? []).filter((c) => c.enabled),
    bankAccounts: (data?.bankAccounts ?? []).filter((a) => a.enabled),
    lastUsedFunding: data?.lastUsedFunding ?? null,
    loading: !!linkId && (loading || data === null),
    refresh,
  };
};
